<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageStamp } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  venue: {
    type: Object,
    required: true,
  },
  spaceCount: {
    type: Number,
    default: 0,
  },
  showBuzz: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['edit', 'delete']);

const { t } = useI18n();

const readableDate = date => {
  if (!date) return '';
  const timestamp = new Date(date).getTime() / 1000;
  return messageStamp(timestamp, 'LLL d, yyyy');
};

const locationLabel = computed(() => {
  const parts = [props.venue.city, props.venue.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
});
</script>

<template>
  <tr>
    <td
      class="py-4 ltr:pr-4 rtl:pl-4 min-w-[200px] font-medium text-n-slate-12"
    >
      {{ venue.name }}
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4 min-w-[180px] text-sm text-n-slate-11">
      {{ locationLabel }}
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4 min-w-[80px] text-sm text-n-slate-11">
      {{ spaceCount }}
    </td>
    <td v-if="showBuzz" class="py-4 ltr:pr-4 rtl:pl-4 min-w-[80px]">
      <span
        v-if="venue.buzz_score != null"
        class="inline-flex items-center gap-1 rounded-full bg-n-brand/10 px-2 py-0.5 text-xs font-medium text-n-brand"
      >
        <Icon icon="i-ph-hands-clapping" class="size-3 shrink-0" />
        {{ venue.buzz_score }}
      </span>
      <span v-else class="text-n-slate-9 text-sm">—</span>
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4 min-w-[120px] text-sm text-n-slate-11">
      {{ readableDate(venue.created_at) }}
    </td>
    <td class="py-4 min-w-xs">
      <div class="flex gap-1 justify-end flex-shrink-0">
        <Button
          v-tooltip.top="t('SETTINGS.SPACES.VENUES.EDIT')"
          icon="i-lucide-pen-line"
          slate
          md
          faded
          :is-loading="loading"
          @click="$emit('edit', venue)"
        />
        <Button
          v-tooltip.top="t('SETTINGS.SPACES.VENUES.DELETE')"
          icon="i-lucide-trash-2"
          md
          ruby
          faded
          :is-loading="loading"
          @click="$emit('delete', venue)"
        />
      </div>
    </td>
  </tr>
</template>
